import { useState, useRef, useEffect } from 'react';
import { uploadPDFForChat, askPDFQuestion, askPaperQuestion } from '../api';

const STARTER_QUESTIONS = [
  'What is the main finding of this paper?',
  'What methods were used?',
  'What are the limitations?',
];

const TABS = [
  { id: 'paper', label: 'Workspace Paper' },
  { id: 'pdf', label: 'Upload PDF' },
];

function ThinkingIndicator() {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      <span className="w-2 h-2 bg-[#808080] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-2 h-2 bg-[#808080] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-2 h-2 bg-[#808080] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

function SpinnerIcon() {
  return (
    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="w-10 h-10 text-[#ebebeb]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

export default function ChatWithPDF({ papers = [], isPaidPlan = true }) {
  const [mode, setMode] = useState('paper');
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [pdfSession, setPdfSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [sendError, setSendError] = useState('');
  const [copiedIdx, setCopiedIdx] = useState(null);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setMessages([]);
    setSendError('');
    if (newMode === 'paper') {
      setPdfSession(null);
    } else {
      setSelectedPaper(null);
    }
  };

  const handlePaperSelect = (e) => {
    const paperId = e.target.value;
    const paper = papers.find((p) => p.id === paperId) || null;
    setSelectedPaper(paper);
    setMessages([]);
    setSendError('');
  };

  const handleFileUpload = async (file) => {
    if (!file || file.type !== 'application/pdf') return;
    setUploadLoading(true);
    setUploadError('');
    setPdfSession(null);
    setMessages([]);
    setSendError('');
    try {
      const response = await uploadPDFForChat(file);
      setPdfSession(response.data);
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Failed to upload PDF. Please try again.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const isReady =
    (mode === 'paper' && selectedPaper !== null) ||
    (mode === 'pdf' && pdfSession !== null);

  const handleSend = async (questionOverride) => {
    const question = (questionOverride ?? input).trim();
    if (!question || loading || !isReady) return;

    const userMessage = { role: 'user', content: question };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setSendError('');
    setLoading(true);

    try {
      let answer;
      if (mode === 'pdf') {
        const response = await askPDFQuestion(pdfSession.session_id, question);
        answer = response.data.answer;
      } else {
        const history = newMessages.slice(0, -1);
        const response = await askPaperQuestion(selectedPaper, question, history);
        answer = response.data.answer;
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: answer }]);
    } catch (err) {
      setSendError(err.response?.data?.detail || 'Failed to get a response. Please try again.');
      setMessages(newMessages.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = (content, idx) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    });
  };

  if (!isPaidPlan) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Chat with a Paper</h2>
          <p className="text-sm text-[#4d4d4d] mt-1">
            Ask questions about a paper in your workspace or upload a PDF directly.
          </p>
        </div>
        <div
          className="bg-white rounded-lg p-8 flex flex-col items-center text-center gap-4"
          style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
        >
          <LockIcon />
          <div>
            <p className="text-[#171717] font-semibold text-sm">Pro Feature</p>
            <p className="text-[#808080] text-sm mt-1 max-w-sm">
              Chat with Paper is available on paid plans only.
            </p>
          </div>
          <a href="/pricing" className="px-5 py-2 bg-[#171717] text-white text-sm font-medium rounded-md hover:bg-[#2e2e2e] transition-colors">
            Upgrade to Pro
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Chat with a Paper</h2>
        <p className="text-sm text-[#4d4d4d] mt-1">
          Ask questions about a paper in your workspace or upload a PDF directly.
        </p>
      </div>

      {/* Mode tabs */}
      <div className="flex" style={{ borderBottom: '1px solid #ebebeb' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleModeChange(tab.id)}
            className="px-5 py-2.5 text-sm font-medium transition-colors"
            style={{
              color: mode === tab.id ? '#171717' : '#808080',
              borderBottom: mode === tab.id ? '2px solid #171717' : '2px solid transparent',
              background: 'transparent',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Source selector */}
      <div className="bg-white rounded-lg p-4" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
        {mode === 'paper' ? (
          <>
            <label className="block text-sm font-medium text-[#171717] mb-1.5" style={{ letterSpacing: '-0.32px' }}>
              Select a paper from your workspace
            </label>
            {papers.length === 0 ? (
              <p className="text-sm text-amber-700 bg-amber-50 rounded px-3 py-2" style={{ boxShadow: 'rgba(251,191,36,0.3) 0px 0px 0px 1px' }}>
                Add papers to your workspace to chat with them.
              </p>
            ) : (
              <select
                value={selectedPaper?.id ?? ''}
                onChange={handlePaperSelect}
                className="w-full rounded-md px-3 py-2 text-sm text-[#171717] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              >
                <option value="">-- Choose a paper --</option>
                {papers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title?.length > 80 ? p.title.slice(0, 77) + '...' : p.title}
                    {p.year ? ` (${p.year})` : ''}
                  </option>
                ))}
              </select>
            )}
          </>
        ) : (
          <>
            <label className="block text-sm font-medium text-[#171717] mb-1.5" style={{ letterSpacing: '-0.32px' }}>
              Upload a PDF to chat with
            </label>

            {uploadLoading ? (
              <div className="border-2 border-dashed rounded-lg px-4 py-8 text-center" style={{ borderColor: '#ebebeb' }}>
                <div className="flex items-center justify-center gap-2 text-[#4d4d4d] text-sm font-medium">
                  <SpinnerIcon />
                  Extracting text...
                </div>
              </div>
            ) : pdfSession ? (
              <div
                className="rounded-lg px-4 py-3 flex items-center justify-between"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              >
                <div>
                  <p className="text-sm font-medium text-[#171717]">{pdfSession.filename}</p>
                  {pdfSession.page_count != null && (
                    <p className="text-xs text-[#808080] mt-0.5">{pdfSession.page_count} page{pdfSession.page_count !== 1 ? 's' : ''}</p>
                  )}
                </div>
                <button
                  onClick={() => { setPdfSession(null); setMessages([]); setSendError(''); }}
                  className="text-xs text-[#808080] hover:text-[#171717] underline"
                >
                  Change
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                className="border-2 border-dashed rounded-lg px-4 py-8 text-center cursor-pointer transition-colors"
                style={{ borderColor: isDragOver ? '#171717' : '#ebebeb', background: isDragOver ? '#fafafa' : 'transparent' }}
              >
                <svg className="mx-auto h-10 w-10 text-[#ebebeb] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <p className="text-sm text-[#4d4d4d]">
                  Drop a PDF here or{' '}
                  <span className="text-[#171717] font-medium">click to browse</span>
                </p>
                <p className="text-xs text-[#808080] mt-1">PDF files only</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              onChange={handleFileInputChange}
            />

            {uploadError && (
              <p className="mt-2 text-sm text-red-600">{uploadError}</p>
            )}
          </>
        )}
      </div>

      {/* Chat area */}
      {isReady && (
        <div
          className="bg-white rounded-lg flex flex-col"
          style={{ minHeight: '420px', boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
        >
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ maxHeight: '400px' }}>
            {messages.length === 0 && !loading && (
              <div className="space-y-2 pt-2">
                <p className="text-xs text-[#808080] text-center mb-3">Suggested questions to get started</p>
                {STARTER_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => handleSend(q)}
                    className="w-full text-left px-3 py-2 text-sm text-[#4d4d4d] rounded-lg hover:bg-[#fafafa] transition-colors"
                    style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'user' ? (
                  <div className="max-w-[75%] px-4 py-2.5 bg-[#171717] text-white text-sm rounded-2xl rounded-tr-sm leading-relaxed">
                    {msg.content}
                  </div>
                ) : (
                  <div className="max-w-[80%] group relative">
                    <div
                      className="px-4 py-3 bg-white rounded-2xl rounded-tl-sm text-sm text-[#4d4d4d] leading-relaxed whitespace-pre-line"
                      style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px' }}
                    >
                      {msg.content}
                    </div>
                    <button
                      onClick={() => handleCopy(msg.content, idx)}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity px-2 py-0.5 text-xs text-[#808080] hover:text-[#171717] bg-white rounded"
                      style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                      aria-label="Copy response"
                    >
                      {copiedIdx === idx ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-tl-sm" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
                  <ThinkingIndicator />
                </div>
              </div>
            )}

            {sendError && (
              <p className="text-xs text-red-600 text-center">{sendError}</p>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input row */}
          <div className="px-3 py-3 flex items-end gap-2" style={{ borderTop: '1px solid #ebebeb' }}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question... (Enter to send, Shift+Enter for new line)"
              rows={2}
              disabled={loading}
              className="flex-1 rounded-lg px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef] resize-none disabled:opacity-60"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              className="px-4 py-2 bg-[#171717] text-white text-sm font-medium rounded-lg hover:bg-[#2e2e2e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5 self-end"
            >
              {loading ? <SpinnerIcon /> : (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                </svg>
              )}
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
